<template>
  <div
    class="slider"
    :class="{ small, }"
    @mouseenter="stopAuto()"
    @mouseleave="startAuto()"
  >
    <div class="shadow-card">
      <div class="slider-items">
        <!-- @slot Emplacement pour les diapositives. -->
        <slot name="slider-items" />
      </div>
    </div>

    <PaginationBubble
      v-if="!noPagination && sliderItems.length > 1"
      v-model="currentIndex"
      :length="sliderItems ? sliderItems.length : 0"
      :small="paginationSmall"
      :disabled="isMoving"
      @update:modelValue="(index, changeWithLoop) => handlePagination(index, changeWithLoop)"
    />
  </div>
</template>

<script>
import { defineComponent } from "vue";
import PaginationBubble from "../../components/nav/PaginationBubble.vue";

/**
 * Affiche des éléments défilants dans des diapositives (carousel).
 */
export default defineComponent({
  name: "Slider",
  components: {
    PaginationBubble,
  },
  props: {
    /**
     * Index du contenu actuel.
     */
    modelValue: {
      type: Number,
      default: 0,
    },
    /**
     * Type d'animation du carousel (translation horizontale ou fondu).
     */
    animation: {
      type: String,
      default: "normal",
      validator: (value) => ["normal", "fade"].includes(value),
    },
    /**
     * Change automatiquement les diapositives.
     */
    autoplay: {
      type: Boolean,
      default: true,
    },
    /**
     * Temps (en s) avant changement de diapositive ("autoplay" doit être actif).
     */
    interval: {
      type: Number,
      default: 10,
    },
    /**
     * Vitesse de l'animation (en ms).
     */
    speed: {
      type: Number,
      default: 0.5,
    },
    /**
     * Arrête l'autoplay tant que la souris est sur le carousel.
     */
    stopOnHover: {
      type: Boolean,
      default: true,
    },
    /**
     * Réduit la taille des diapositives.
     */
    small: {
      type: Boolean,
      default: false,
    },
    /**
     * Réduit la taille de la pagination.
     */
    paginationSmall: {
      type: Boolean,
      default: false,
    },
    /**
     * S'il faut une pagination pour le slider.
     */
    noPagination: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
     * Déclenché au changement de diapositive.
     * @param {Number} nextIndex Index de la prochaine diapositive.
     */
    "update:modelValue",
  ],
  data() {
    return {
      sliderItems: [],
      oldValue: this.modelValue,
      currentIndex: this.modelValue,
      timer: 0,
      isMovingTimer: 0,
      isStopped: false,
      isMoving: false,
    };
  },
  mounted() {
    this.init();
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  methods: {
    /**
     * Initialise le carousel.
     */
    init() {
      this.sliderItems = this.$children.filter(
        (child) => child.$el.className.split(" ")[0] === "slider-card",
      );

      const currentItem = this.sliderItems[this.currentIndex];

      if (currentItem) {
        currentItem.init();
        this.auto();
      }
    },
    /**
     * Vérifie s'il y a des diapositives pour pouvoir les interchanger.
     * @returns {Boolean} Si true, on peut changer de diapositive.
     */
    canMove() {
      return this.sliderItems.length > 1 && !this.isMoving;
    },
    /**
     * Récupère le prochain index suivant la direction.
     * @param {String} direction Direction du changement de diapositives ("left" ou "right").
     * @returns {Number} Si true, on peut changer de diapositive.
     */
    getNextIndex(direction) {
      if (direction === "left" && this.currentIndex + 1 >= this.sliderItems.length) {
        return 0;
      }
      if (direction === "right") {
        if (this.currentIndex - 1 < 0) {
          return 0;
        }
        return this.currentIndex - 1;
      }
      return this.currentIndex + 1;
    },
    /**
     * Change de diapositive.
     * @param {Number} index Index de la diapositive actuelle.
     * @param {Number} nextIndex Index de la prochaine diapositive.
     * @param {Boolean} changeWithLoop Si true, la diapositive change en passant de la dernière à la
     *                                 première (ou inversement).
     */
    move(index, nextIndex, changeWithLoop) {
      let direction = index < nextIndex ? "left" : "right";

      if (this.sliderItems.length > 2) {
        if (index === 0 && nextIndex === this.sliderItems.length - 1 && changeWithLoop) {
          direction = "right";
        }
        if (index === this.sliderItems.length - 1 && nextIndex === 0 && changeWithLoop) {
          direction = "left";
        }
      }

      const currentItem = this.sliderItems[index];
      const nextItem = this.sliderItems[nextIndex];
      if (nextItem) {
        currentItem.hide(direction);
        nextItem.show(direction);

        if (this.isMovingTimer) {
          clearInterval(this.isMovingTimer);
        }

        this.isMoving = true;
        this.isMovingTimer = setInterval(() => {
          this.isMoving = false;
        }, this.speed * 1000);

        this.$emit("update:modelValue", nextIndex);
      }
    },
    /**
     * Gère le changement de diapositive via la pagination.
     * @param {Number} index Index reçu de la pagination.
     * @param {Boolean} changeWithLoop Si true, la diapositive change en passant de la dernière à
     *                                 la première (ou inversement).
     */
    handlePagination(index, changeWithLoop) {
      if (this.canMove()) {
        this.stopAuto();
        this.move(this.oldValue, index, changeWithLoop);
        this.oldValue = index;
        this.startAuto();
      }
    },
    /**
     * Gère le changement de diapositive via l'intervalle de temps.
     * @param {Number} index Index de la diapositive actuelle.
     */
    handleAutoChange(index) {
      if (this.canMove()) {
        this.stopAuto();
      }

      const direction = index > this.currentIndex ? "left" : "right";
      const nextIndex = this.getNextIndex(direction);

      this.move(this.currentIndex, nextIndex, true);
      this.currentIndex = nextIndex;
      this.oldValue = nextIndex;
      this.startAuto();
    },
    /**
     * Change la diapositive automatiquement après un intervalle défini.
     */
    auto() {
      if (this.autoplay && !this.isStopped) {
        if (this.timer) {
          clearInterval(this.timer);
        }
        this.timer = setInterval(() => {
          this.handleAutoChange(this.currentIndex + 1);
        }, this.interval * 1000);
      }
    },
    /**
     * Démarre le changement automatique des diapositives.
     */
    startAuto() {
      if (this.autoplay && this.stopOnHover) {
        this.isStopped = false;
        this.auto();
      }
    },
    /**
     * Arrête le changement automatique des diapositives.
     */
    stopAuto() {
      if (this.autoplay && this.stopOnHover) {
        this.isStopped = true;
        if (this.timer) {
          clearInterval(this.timer);
        }
      }
    },
  },
});
</script>

<style lang="scss">
@use "../../assets/styles/components/sliders/slider.scss";
</style>
